import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import {useSearchParams} from 'react-router-dom'
import {DateTime} from 'luxon'
import {useTranslation} from 'react-i18next'
import type {PatientReportSlotResponse} from '@common/models'
import {MISSING_ID} from '@common/utils'

import {PatientsToolbar} from './PatientsToolbar'
import {SlotItemTooltip} from './SlotItemTooltip'
import {SlotDetailsModal} from './SlotDetailsModal'
import {ServiceDetailsModal} from './ServiceDetailsModal'
import {
  DayHeader,
  GridWrapper,
  PageContainer,
  PatientsWeekTimeline,
  ServiceHeaderFull,
  ServiceNameButton,
  ServicesStack,
  SlotItem,
  WeekHeaderRow,
  DayColumn,
  ServiceSection,
  SlotClickTarget,
} from './ReportsPatientsPage.styled'

import {
  useGetPartnerPatientsReport,
  useExportPatientsReportPdf,
  useExportPatientsReportXlsx,
} from '~/api/partnerAdminReports'
import {useGetPartnerServices} from '~/api/partnerAdminServices'
import {useGetPractitionerPatientsReport} from '~/api'
import {Typography} from '~/components/general/typography/Typography'
import {ProcessingOverlay} from '~/components/general/processingOverlay/ProcessingOverlay'
import {PractitionerContext} from '~/components/profile/PractitionerContext'
import {useNavigationType} from '~/hooks/useNavigationType'

const formatYyMmDd = (dt: DateTime) => dt.toFormat('yyLLdd')
const toMonday = (dt: DateTime) =>
  dt.minus({days: (dt.weekday + 6) % 7}).startOf('day') // Monday-first, independent of locale

export const ReportsPatientsPage = () => {
  const {t} = useTranslation()
  const {isPractitionerOnly} = useNavigationType()
  const {selectedEmployment} = useContext(PractitionerContext)
  const facilityId = selectedEmployment?.facilityId ?? MISSING_ID
  const [searchParams, setSearchParams] = useSearchParams()
  const [centerDate, setCenterDate] = useState<DateTime>(
    toMonday(DateTime.now()),
  )
  const initialServiceId = searchParams.get('serviceId') || ''
  const [selectedServiceId, setSelectedServiceId] =
    useState<string>(initialServiceId)
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null)
  const [selectedServiceIdForDetails, setSelectedServiceIdForDetails] =
    useState<string | null>(null)

  const yymmdd = useMemo(() => formatYyMmDd(centerDate), [centerDate])
  // If coming from Services (serviceId in URL), the first backend call should not include date.
  const suppressDateForFirstLoadRef = useRef<boolean>(
    !isPractitionerOnly && !!initialServiceId,
  )
  const didSyncCenterFromApiRef = useRef<boolean>(false)
  const yymmddForApi = suppressDateForFirstLoadRef.current ? undefined : yymmdd

  const reportQueryOptions = {
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always' as const,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  }

  const {data: services} = useGetPartnerServices(undefined, {
    enabled: !isPractitionerOnly,
  })
  const serviceOptions = useMemo(
    () =>
      [{label: t('All services'), value: ''}].concat(
        (services || []).map((s) => ({label: s.name, value: s.id})),
      ),
    [services, t],
  )

  const {data: partnerReport, isLoading: isPartnerReportLoading} =
    useGetPartnerPatientsReport(yymmddForApi, selectedServiceId || undefined, {
      ...reportQueryOptions,
      enabled: !isPractitionerOnly,
    })
  const {data: practitionerReport, isLoading: isPractitionerReportLoading} =
    useGetPractitionerPatientsReport(facilityId, yymmdd, {
      ...reportQueryOptions,
      enabled: isPractitionerOnly,
    })
  const data = isPractitionerOnly ? practitionerReport : partnerReport
  const isLoading = isPractitionerOnly
    ? isPractitionerReportLoading
    : isPartnerReportLoading

  const {mutate: exportPdf, isPending: isExportingPdf} =
    useExportPatientsReportPdf(selectedServiceId || '')
  const {mutate: exportXlsx, isPending: isExportingXlsx} =
    useExportPatientsReportXlsx(selectedServiceId || '')

  const isExporting = !isPractitionerOnly && (isExportingPdf || isExportingXlsx)

  const handleExportPdf = () => {
    if (selectedServiceId) {
      exportPdf()
    }
  }

  const handleExportExcel = () => {
    if (selectedServiceId) {
      exportXlsx()
    }
  }

  // When initial load comes from Services (no date sent), reposition the grid to the week
  // that the backend returned, based on the earliest day present in the payload.
  useEffect(() => {
    if (!suppressDateForFirstLoadRef.current) return
    if (didSyncCenterFromApiRef.current) return
    const servicesData = data?.services || []
    const allDates: string[] = []
    servicesData.forEach((s) => s.days.forEach((d) => allDates.push(d.date)))
    if (allDates.length === 0) return
    const firstStr = allDates.sort()[0] // yyyy/MM/dd lexicographically sorts correctly
    const dt = DateTime.fromFormat(firstStr, 'yyyy/MM/dd')
    if (!dt.isValid) return
    setCenterDate(toMonday(dt))
    didSyncCenterFromApiRef.current = true
    // Intentionally KEEP suppressDateForFirstLoadRef = true so we don't trigger a refetch
    // with yymmdd immediately; it will be disabled when the user navigates weeks or changes the filter.
  }, [data, setCenterDate])

  const weekDays = useMemo(() => {
    const monday = toMonday(centerDate)
    return Array.from({length: 7}).map((_, i) => monday.plus({days: i}))
  }, [centerDate])

  // Index by serviceId -> date (yyyy/MM/dd) -> items
  const byServiceAndDay = useMemo(() => {
    const map = new Map<
      string,
      {
        name: string
        location?: string
        contacts?: string
        days: Map<
          string,
          {serviceName: string; slots: PatientReportSlotResponse[]}
        >
      }
    >()
    ;(data?.services || []).forEach((svc) => {
      const days = new Map<
        string,
        {serviceName: string; slots: PatientReportSlotResponse[]}
      >()
      svc.days.forEach((d) => {
        days.set(d.date, {serviceName: svc.serviceName, slots: d.slots})
      })
      map.set(svc.serviceId, {
        name: svc.serviceName,
        location: svc.location,
        contacts: svc.contacts,
        days,
      })
    })
    return map
  }, [data?.services])

  const dateKeys = useMemo(
    () => weekDays.map((d) => d.toFormat('yyyy/MM/dd')),
    [weekDays],
  )

  // Find the selected slot to get its PatientId, serviceId, and status
  const selectedSlot = useMemo(() => {
    if (!selectedSlotId) return null
    return (
      Array.from(byServiceAndDay.entries())
        .map(([serviceId, svc]) => {
          const dayEntries = Array.from(svc.days.values())
          const foundDay = dayEntries.find((day) =>
            day.slots.some((s) => s.slotId === selectedSlotId),
          )
          if (foundDay) {
            const slot = foundDay.slots.find((s) => s.slotId === selectedSlotId)
            return slot ? {...slot, serviceId} : null
          }
          return null
        })
        .find((result) => result !== null) || null
    )
  }, [selectedSlotId, byServiceAndDay])

  const selectedServiceForDetails = selectedServiceIdForDetails
    ? byServiceAndDay.get(selectedServiceIdForDetails)
    : undefined

  return (
    <PageContainer direction="column" spacing={2} flex fullWidth>
      <PatientsToolbar
        onPrevWeek={() => {
          suppressDateForFirstLoadRef.current = false
          setCenterDate((d) => d.minus({weeks: 1}))
        }}
        onNextWeek={() => {
          suppressDateForFirstLoadRef.current = false
          setCenterDate((d) => d.plus({weeks: 1}))
        }}
        serviceOptions={serviceOptions}
        selectedServiceId={selectedServiceId}
        onServiceChange={(id) => {
          setSelectedServiceId(id)
          suppressDateForFirstLoadRef.current = false
          const current = Object.fromEntries(searchParams.entries())
          if (id) {
            setSearchParams({...current, serviceId: id})
          } else {
            const rest = Object.fromEntries(
              Object.entries(current).filter(([key]) => key !== 'serviceId'),
            )
            setSearchParams(rest)
          }
        }}
        isExportDisabled={!selectedServiceId}
        onExportPdf={handleExportPdf}
        onExportExcel={handleExportExcel}
        showServiceFilter={!isPractitionerOnly}
        showExports={!isPractitionerOnly}
      />

      <GridWrapper direction="column" spacing={2} fullWidth flex>
        {isLoading && (
          <Typography size="sm" weight="normal">
            {t('Loading...')}
          </Typography>
        )}

        <PatientsWeekTimeline>
          <WeekHeaderRow>
            {weekDays.map((d, idx) => (
              <DayHeader key={idx} align="center" justify="center">
                <Typography weight="medium">
                  {d.toLocaleString({weekday: 'short'})}{' '}
                  {d.toLocaleString(DateTime.DATE_SHORT)}
                </Typography>
              </DayHeader>
            ))}
          </WeekHeaderRow>

          <ServicesStack>
            {Array.from(byServiceAndDay.entries()).map(([svcId, svc]) => (
              <ServiceSection key={svcId}>
                <ServiceHeaderFull>
                  <ServiceNameButton
                    type="button"
                    onClick={() => setSelectedServiceIdForDetails(svcId)}>
                    <Typography
                      inline
                      weight="medium"
                      textDecoration="underline">
                      {svc.name}
                    </Typography>
                  </ServiceNameButton>
                </ServiceHeaderFull>
                {dateKeys.map((dk, colIdx) => {
                  const day = svc.days.get(dk)
                  const slots = day?.slots || []
                  return (
                    <DayColumn
                      key={`${svcId}-${dk}`}
                      direction="column"
                      spacing={0.5}
                      $col={colIdx + 1}>
                      {slots.map((slot, i) => {
                        const slotItem = (
                          <SlotItem
                            $status={slot.status}
                            flex
                            direction="row"
                            spacing={1}>
                            <Typography
                              size="sm"
                              weight="medium"
                              flexShrink={0}>
                              {slot.time}
                            </Typography>
                            {!!slot.patient && (
                              <Typography size="sm" ellipsis flexGrow={1}>
                                {slot.patient}
                              </Typography>
                            )}
                          </SlotItem>
                        )
                        return (
                          <SlotItemTooltip slot={slot} key={slot.slotId || i}>
                            {isPractitionerOnly ? (
                              slotItem
                            ) : (
                              <SlotClickTarget
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedSlotId(slot.slotId)}
                                onKeyDown={(
                                  e: KeyboardEvent<HTMLDivElement>,
                                ) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    setSelectedSlotId(slot.slotId)
                                  }
                                }}>
                                {slotItem}
                              </SlotClickTarget>
                            )}
                          </SlotItemTooltip>
                        )
                      })}
                    </DayColumn>
                  )
                })}
              </ServiceSection>
            ))}
          </ServicesStack>
        </PatientsWeekTimeline>

        {!isLoading &&
          (!data?.services?.length ||
            data.services.every((s) =>
              s.days.every((d) => !d.slots.length),
            )) && (
            <Typography size="sm">{t('No data for this week')}</Typography>
          )}
      </GridWrapper>

      <ServiceDetailsModal
        isVisible={!!selectedServiceForDetails}
        onClose={() => setSelectedServiceIdForDetails(null)}
        serviceName={selectedServiceForDetails?.name || ''}
        location={selectedServiceForDetails?.location}
        contacts={selectedServiceForDetails?.contacts}
      />
      <SlotDetailsModal
        isVisible={!isPractitionerOnly && !!selectedSlotId}
        onClose={() => setSelectedSlotId(null)}
        slotId={selectedSlotId}
        patientId={selectedSlot?.patientId || null}
        partnerServiceId={selectedSlot?.serviceId || null}
        slotStatus={selectedSlot?.status}
        showViewPatientButton
      />
      <ProcessingOverlay isVisible={isLoading || isExporting} />
    </PageContainer>
  )
}
